// src/components/RoddoRadioNowPlayingBanner.tsx
import React, { useMemo, useState } from 'react';
import { useRoddoRadio } from '../context/RoddoRadioProvider';
import {
  roddoRadioMoods,
  type RoddoRadioMoodId,
  type RoddoRadioTrack,
} from '../data/roddoRadioTracks';

interface RoddoRadioNowPlayingBannerProps {
  isDarkMode?: boolean;
  placement?: 'top' | 'bottom';
}

type PlaylistFilter = 'all' | RoddoRadioMoodId;

const stopMapEvent = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

function formatTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const minutes = Math.floor(seconds / 60);
  const remainder = Math.floor(seconds % 60);
  return `${minutes}:${String(remainder).padStart(2, '0')}`;
}

const RoddoRadioNowPlayingBanner: React.FC<RoddoRadioNowPlayingBannerProps> = ({
  isDarkMode = false,
  placement = 'top',
}) => {
  const isBottomDock = placement === 'bottom';
  const [isOpen, setIsOpen] = useState(false);
  const [isPlaylistOpen, setIsPlaylistOpen] = useState(false);
  const [isVolumeOpen, setIsVolumeOpen] = useState(false);
  const [playlistFilter, setPlaylistFilter] = useState<PlaylistFilter>('all');
  const [isQuietVisible, setIsQuietVisible] = useState(true);
  const quietHideTimerRef = React.useRef<number | null>(null);
  const shellRef = React.useRef<HTMLDivElement | null>(null);

  const {
    tracks,
    currentTrack,
    isPlaying,
    isMuted,
    volume,
    duration,
    currentTime,
    togglePlay,
    previousTrack,
    nextTrack,
    toggleMute,
    setVolume,
    seekTo,
    selectTrack,
  } = useRoddoRadio();

  React.useEffect(() => {
    const onToggle = () => {
      setIsQuietVisible(true);
      setIsOpen((open) => !open);
      setIsPlaylistOpen(false);
      setIsVolumeOpen(false);
    };

    window.addEventListener('roddo-radio-map-toggle', onToggle);
    return () => window.removeEventListener('roddo-radio-map-toggle', onToggle);
  }, []);


  React.useEffect(() => {
    if (!isOpen) return;

    const handleOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && shellRef.current?.contains(target)) return;

      setIsOpen(false);
      setIsPlaylistOpen(false);
      setIsVolumeOpen(false);
      setIsQuietVisible(true);
    };

    document.addEventListener('pointerdown', handleOutsidePointerDown, true);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown, true);
  }, [isOpen]);

  React.useEffect(() => {
    if (quietHideTimerRef.current) {
      window.clearTimeout(quietHideTimerRef.current);
      quietHideTimerRef.current = null;
    }

    setIsQuietVisible(true);

    if (!isOpen) {
      quietHideTimerRef.current = window.setTimeout(() => {
        setIsQuietVisible(false);
      }, 5200);
    }

    return () => {
      if (quietHideTimerRef.current) {
        window.clearTimeout(quietHideTimerRef.current);
        quietHideTimerRef.current = null;
      }
    };
  }, [currentTrack?.id, isPlaying, isMuted, isOpen]);

  const textColor = isDarkMode ? '#FFF7E8' : '#253343';
  const mutedTextColor = isDarkMode
    ? 'rgba(255,247,232,0.62)'
    : 'rgba(92,78,66,0.62)';
  const shellBackground = isDarkMode ? 'rgba(18,26,43,0.94)' : 'rgba(255,252,247,0.94)';
  const shellBorder = isDarkMode ? 'rgba(180,195,220,0.14)' : 'rgba(74,58,42,0.12)';
  const buttonBackground = isDarkMode ? '#182238' : '#F2EBE1';
  const buttonBorder = isDarkMode
    ? '1px solid rgba(255,255,255,0.10)'
    : '1px solid rgba(91,67,49,0.10)';
  const popoverBackground = isDarkMode ? '#121A2B' : '#FFF9F1';

  const effectiveVolume = isMuted ? 0 : volume;
  const volumeIcon = effectiveVolume <= 0 ? '🔇' : effectiveVolume < 0.45 ? '🔉' : '🔊';

  const filteredTracks = useMemo(() => {
    if (playlistFilter === 'all') return tracks;
    return tracks.filter((track) => track.moodId === playlistFilter);
  }, [playlistFilter, tracks]);

  const currentMood = roddoRadioMoods.find((mood) => mood.id === currentTrack?.moodId);

  const handleTrackSelect = (track: RoddoRadioTrack, playNow = false) => {
    selectTrack(track, { play: playNow });
  };

  React.useEffect(() => {
    if (!isBottomDock) return;

    const visible = isOpen || isQuietVisible;
    window.dispatchEvent(
      new CustomEvent('roddo-radio-map-visibility', {
        detail: { visible, open: isOpen },
      }),
    );

    return () => {
      window.dispatchEvent(
        new CustomEvent('roddo-radio-map-visibility', {
          detail: { visible: false, open: false },
        }),
      );
    };
  }, [isBottomDock, isOpen, isQuietVisible]);

  if (!currentTrack) return null;

  return (
    <div
      ref={shellRef}
      className={[
        'relative mx-auto transition-all duration-500 ease-out',
        isBottomDock ? 'w-full max-w-[304px]' : 'w-[min(92%,390px)]',
        isOpen || isQuietVisible
          ? isBottomDock
            ? isOpen
              ? 'pointer-events-auto mb-0 max-h-[420px] overflow-visible opacity-100 translate-y-0'
              : 'pointer-events-auto mb-0 max-h-[420px] overflow-visible opacity-100 translate-y-0 min-[1024px]:pointer-events-none min-[1024px]:max-h-0 min-[1024px]:overflow-hidden min-[1024px]:opacity-0 min-[1024px]:translate-y-2'
            : 'pointer-events-auto mt-2 max-h-[420px] overflow-visible opacity-100 translate-y-0'
          : isBottomDock
            ? 'pointer-events-none mb-0 max-h-0 overflow-hidden opacity-0 translate-y-2'
            : 'pointer-events-none mt-0 max-h-0 overflow-hidden opacity-0 -translate-y-1',
      ].join(' ')}
      onClick={stopMapEvent}
      onMouseDown={stopMapEvent}
      onPointerDown={stopMapEvent}
      onTouchStart={stopMapEvent}
      aria-live="polite"
    >
      <div
        className={isBottomDock ? "relative overflow-visible rounded-[18px] border px-2.5 py-2 shadow-sm backdrop-blur-xl transition-all" : "relative overflow-visible rounded-[20px] border px-3 py-2 shadow-sm backdrop-blur-xl transition-all"}
        style={{
          background: shellBackground,
          borderColor: shellBorder,
          color: textColor,
          boxShadow: isDarkMode
            ? '0 5px 14px rgba(0,0,0,0.20)'
            : '0 4px 12px rgba(89,65,37,0.06)',
        }}
      >
        <div className={isBottomDock ? "flex min-w-0 items-center gap-1.5" : "flex min-w-0 items-center gap-2"}>
          <span
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
            style={{
              background: isPlaying ? '#8FD0CA' : 'rgba(142,126,105,0.46)',
              boxShadow: isPlaying ? '0 0 10px rgba(143,208,202,0.58)' : 'none',
            }}
          />

          <button
            type="button"
            aria-label="Open Roddo Radio playlist"
            aria-expanded={isPlaylistOpen}
            onClick={() => {
              if (!isOpen) {
                setIsOpen(true);
                return;
              }
              setIsPlaylistOpen((open) => !open);
              setIsVolumeOpen(false);
              if (!isPlaylistOpen && currentTrack?.moodId) setPlaylistFilter(currentTrack.moodId);
            }}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left active:scale-[0.99]"
            style={{ color: textColor }}
          >
            <div
              className="truncate text-[8px] font-black uppercase tracking-[0.18em]"
              style={{ color: mutedTextColor }}
            >
              {isOpen ? `Roddo Radio · ${currentMood?.label || 'Now Playing'}` : 'Now playing'}
            </div>
            <div className={isBottomDock ? "truncate text-[10.5px] font-black leading-tight" : "truncate text-[11px] font-black leading-tight"}>
              {isOpen
                ? `${currentTrack.title} ▾`
                : `${currentTrack.title} : ${currentTrack.moodLabel}`}
            </div>
          </button>

          {isOpen ? (
            <>
              <button
                type="button"
                aria-label="Previous radio track"
                onClick={previousTrack}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[15px] font-black leading-none active:scale-95"
                style={{ background: buttonBackground, border: buttonBorder, color: textColor }}
              >
                ‹
              </button>

              <button
                type="button"
                aria-label={isPlaying ? 'Pause Roddo Radio' : 'Play Roddo Radio'}
                onClick={togglePlay}
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[13px] font-black leading-none shadow-sm active:scale-95"
                style={{
                  background: '#FFC94A',
                  color: '#263445',
                  border: '1px solid rgba(255,255,255,0.48)',
                  boxShadow: '0 4px 0 rgba(206,127,36,0.25)',
                }}
              >
                {isPlaying ? '❚❚' : '▶'}
              </button>

              <button
                type="button"
                aria-label="Next radio track"
                onClick={nextTrack}
                className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[15px] font-black leading-none active:scale-95"
                style={{ background: buttonBackground, border: buttonBorder, color: textColor }}
              >
                ›
              </button>

              <div className="relative shrink-0">
                {isVolumeOpen ? (
                  <div
                    className={[
                      'absolute right-[-4px] z-[90] flex h-[136px] w-11 flex-col items-center justify-between rounded-[18px] border px-2 py-2 shadow-lg backdrop-blur-xl',
                      isBottomDock ? 'bottom-[calc(100%+10px)]' : 'top-[calc(100%+10px)]',
                    ].join(' ')}
                    onClick={stopMapEvent}
                    onMouseDown={stopMapEvent}
                    onPointerDown={stopMapEvent}
                    onTouchStart={stopMapEvent}
                    style={{
                      background: popoverBackground,
                      borderColor: isDarkMode ? 'rgba(255,255,255,0.16)' : 'rgba(91,67,49,0.14)',
                      boxShadow: isDarkMode
                        ? '0 16px 34px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.10)'
                        : '0 16px 34px rgba(89,65,37,0.18), inset 0 1px 0 rgba(255,255,255,0.86)',
                    }}
                  >
                    <div className="relative flex h-[82px] w-7 items-center justify-center">
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={effectiveVolume}
                        onChange={(event) => setVolume(Number(event.currentTarget.value))}
                        onClick={stopMapEvent}
                        onMouseDown={stopMapEvent}
                        onPointerDown={stopMapEvent}
                        onTouchStart={stopMapEvent}
                        aria-label="Roddo Radio volume"
                        className="absolute h-5 w-[82px] cursor-pointer accent-[#2F80ED] dark:accent-[#7057E8]"
                        style={{ transform: 'rotate(-90deg)', touchAction: 'none' }}
                      />
                    </div>

                    <button
                      type="button"
                      aria-label={isMuted || volume <= 0 ? 'Unmute Roddo Radio' : 'Mute Roddo Radio'}
                      onClick={toggleMute}
                      className="grid h-7 w-7 place-items-center rounded-full text-[13px] active:scale-95"
                      style={{
                        background: effectiveVolume <= 0 ? 'rgba(244,168,63,0.24)' : buttonBackground,
                        border: buttonBorder,
                        color: textColor,
                      }}
                    >
                      🔇
                    </button>
                  </div>
                ) : null}

                <button
                  type="button"
                  aria-label="Open Roddo Radio volume control"
                  aria-expanded={isVolumeOpen}
                  onClick={() => {
                    setIsVolumeOpen((open) => !open);
                    setIsPlaylistOpen(false);
                  }}
                  className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[13px] font-black leading-none active:scale-95"
                  style={{
                    background: isVolumeOpen
                      ? isDarkMode ? 'rgba(112,87,232,0.14)' : 'rgba(47,128,237,0.08)'
                      : buttonBackground,
                    border: buttonBorder,
                    color: textColor,
                  }}
                >
                  {volumeIcon}
                </button>
              </div>
            </>
          ) : null}
        </div>

        {isOpen ? (
          <div className={isBottomDock ? "mt-1 grid grid-cols-[28px_minmax(0,1fr)_28px] items-center gap-1" : "mt-1.5 grid grid-cols-[30px_minmax(0,1fr)_30px] items-center gap-1.5"}>
            <span className="text-[9px] font-black tabular-nums" style={{ color: mutedTextColor }}>
              {formatTime(currentTime)}
            </span>
            <input
              type="range"
              min={0}
              max={Math.max(0, duration || 0)}
              step={0.25}
              value={Math.min(currentTime, duration || currentTime || 0)}
              onChange={(event) => seekTo(Number(event.currentTarget.value))}
              onClick={stopMapEvent}
              onMouseDown={stopMapEvent}
              onPointerDown={stopMapEvent}
              onTouchStart={stopMapEvent}
              aria-label="Roddo Radio song position"
              className={isBottomDock ? "h-3 w-full min-w-0 cursor-pointer accent-[#F4A83F]" : "h-4 w-full min-w-0 cursor-pointer accent-[#F4A83F]"}
            />
            <span className="text-right text-[9px] font-black tabular-nums" style={{ color: mutedTextColor }}>
              {formatTime(duration)}
            </span>
          </div>
        ) : null}
      </div>

      {isOpen && isPlaylistOpen ? (
        <div
          className={[
            'absolute left-0 right-0 z-[4050] max-h-[300px] overflow-hidden rounded-[20px] border p-2.5 shadow-xl backdrop-blur-xl',
            isBottomDock ? 'bottom-[calc(100%+8px)]' : 'top-[calc(100%+8px)]',
          ].join(' ')}
          onClick={stopMapEvent}
          onMouseDown={stopMapEvent}
          onPointerDown={stopMapEvent}
          onTouchStart={stopMapEvent}
          style={{
            background: popoverBackground,
            borderColor: isDarkMode ? 'rgba(255,255,255,0.16)' : 'rgba(91,67,49,0.14)',
            boxShadow: isDarkMode
              ? '0 18px 42px rgba(0,0,0,0.42), inset 0 1px 0 rgba(255,255,255,0.10)'
              : '0 18px 42px rgba(89,65,37,0.20), inset 0 1px 0 rgba(255,255,255,0.86)',
          }}
        >
          <div className="mb-2 flex gap-3 overflow-x-auto pb-1 text-[10px] font-black uppercase tracking-[0.12em]">
            <button
              type="button"
              onClick={() => setPlaylistFilter('all')}
              className="shrink-0 border-0 bg-transparent px-0 py-1 active:scale-95"
              style={{
                color: playlistFilter === 'all' ? '#D1842F' : mutedTextColor,
                opacity: playlistFilter === 'all' ? 1 : 0.72,
              }}
            >
              All
            </button>
            {roddoRadioMoods.map((mood) => {
              const selected = playlistFilter === mood.id;
              return (
                <button
                  key={mood.id}
                  type="button"
                  onClick={() => setPlaylistFilter(mood.id)}
                  className="shrink-0 border-0 bg-transparent px-0 py-1 active:scale-95"
                  style={{
                    color: selected ? '#D1842F' : mutedTextColor,
                    opacity: selected ? 1 : 0.72,
                  }}
                >
                  {mood.label}
                </button>
              );
            })}
          </div>

          <div className="max-h-[232px] space-y-1 overflow-y-auto pr-1">
            {filteredTracks.map((track) => {
              const isCurrent = track.id === currentTrack?.id;
              return (
                <div
                  key={track.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleTrackSelect(track, false)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleTrackSelect(track, false);
                    }
                  }}
                  className="group flex cursor-pointer items-center gap-2 rounded-[14px] px-2.5 py-2 transition-colors active:scale-[0.99]"
                  style={{
                    background: isCurrent
                      ? isDarkMode
                        ? 'rgba(255,213,106,0.14)'
                        : 'rgba(255,229,142,0.34)'
                      : 'transparent',
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate text-[8px] font-black uppercase tracking-[0.16em]"
                      style={{ color: isCurrent ? '#D1842F' : mutedTextColor }}
                    >
                      {track.moodLabel}
                    </div>
                    <div className="truncate text-[12px] font-black leading-tight" style={{ color: textColor }}>
                      {track.title}
                    </div>
                  </div>

                  <button
                    type="button"
                    aria-label={isCurrent && isPlaying ? `Pause ${track.title}` : `Play ${track.title}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (isCurrent) {
                        togglePlay();
                      } else {
                        handleTrackSelect(track, true);
                      }
                    }}
                    className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-black opacity-0 shadow-sm transition-all group-hover:opacity-100 group-focus-within:opacity-100 active:scale-95"
                    style={{
                      background: '#FFC94A',
                      border: '1px solid rgba(255,255,255,0.46)',
                      color: '#263445',
                    }}
                  >
                    {isCurrent && isPlaying ? '❚❚' : '▶'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RoddoRadioNowPlayingBanner;
